/**
 * Service layer — Slash command data queries
 *
 * Owns cross-domain queries used by the slash command system and My Work. Commands fetch their own
 * data here — they never read from ctx cache.
 *
 * Assignment queries left with RASCI (retired 2026-09-02, docs/FLOW_RECONCILIATION_PLAN.md): who is
 * working on what is the crew index — frontend/src/outline/crew.js over services/flights.js.
 */

import { apiGet, apiGetAll, apiPost } from './_api.js'

const TBL_DEL   = 'tblFuiDpahqCL4eIK'   // Project Deliverables (milestones)
const TBL_PROJ  = 'tblgWX330MPJkhQVq'   // Projects
const TBL_TIME  = 'tblksH7rbKDI4gWeZ'   // Time Entry Staging
const TBL_CL    = 'tblU6m44TpQ9zkpso'   // Deliverable Checklist Items

/**
 * Accomplishments (CT-025 Phase 5a) — checklist items the user has COMPLETED since `sinceYmd`.
 * Drives the My Work streak + this-week tally. No fields[] (lookups). Filter by the member-id text
 * lookup of who completed it.
 * @param {string} memberId
 * @param {string} sinceYmd  YYYY-MM-DD lower bound (e.g. 90 days ago)
 */
export function getMyCompletedChecklist(memberId, sinceYmd) {
  if (!memberId) return Promise.resolve([])
  const formula = 'AND(' +
    '{Complete}=1,' +
    'FIND("' + memberId + '",ARRAYJOIN({Completed by Member Record ID},",")),' +
    "IS_AFTER({Completed Date},'" + sinceYmd + "')" +
  ')'
  return apiGet(TBL_CL, 'filterByFormula=' + encodeURIComponent(formula) + '&maxRecords=400')
}

/**
 * Get milestones past their Latest Finish date that haven't been posted.
 * Do not use fields[] — lookup fields (Project Name) return empty when restricted.
 *
 * @param {string|null} projectId — optional project record ID to scope results
 * @returns {Promise<Array>} Raw Airtable milestone records
 */
export function getOverdueMilestones(projectId) {
  const conditions = [
    'IS_BEFORE({Latest Finish (LFT)}, TODAY())',
    '{Posted Date}=""',
    '{Contract Stage (from Project)}="Executed"',
  ]
  if (projectId) {
    conditions.push('FIND("' + projectId + '",ARRAYJOIN({Project Record UID from Project},","))')
  }
  const formula = 'AND(' + conditions.join(',') + ')'
  return apiGet(
    TBL_DEL,
    'filterByFormula=' + encodeURIComponent(formula) +
    '&sort%5B0%5D%5Bfield%5D=Latest+Finish+(LFT)&sort%5B0%5D%5Bdirection%5D=asc' +
    '&maxRecords=50'
  )
}

/**
 * Get all active projects (Contract Stage = Executed or On Hold — a held project is still active,
 * just paused, and should still be reachable by name here), sorted by project number.
 * Project Name and Project Number are regular fields — fields[] is safe here.
 *
 * Uses apiGetAll (proper offset pagination), not a capped single page — a fixed `maxRecords=50`
 * here previously truncated the list once the firm passed 50 active+on-hold projects, silently
 * dropping whichever sorted last from /go (bug, fixed 2026-08 alongside the identical home-list
 * truncation — see services/projects.js#getOpenProjects).
 *
 * @returns {Promise<Array>} Raw Airtable project records
 */
export function getActiveProjects() {
  return apiGetAll(
    TBL_PROJ,
    'filterByFormula=' + encodeURIComponent('OR({Contract Stage}="Executed",{Contract Stage}="On Hold")') +
    '&fields%5B%5D=Project+Name&fields%5B%5D=Project+Number' +
    '&sort%5B0%5D%5Bfield%5D=Project+Number&sort%5B0%5D%5Bdirection%5D=asc'
  )
}

/**
 * Create a Time Entry Staging record.
 *
 * @param {object} opts
 * @param {string} opts.memberId — Member record ID (submitter)
 * @param {string} opts.phaseId  — Phase record ID (Phase Airtable ID field)
 * @param {number} opts.hours    — Actual hours (e.g. 2.5)
 * @param {string} opts.date     — Entry date as YYYY-MM-DD
 * @param {string} [opts.note]   — Optional memo draft
 * @returns {Promise} Raw Airtable POST response
 */
export async function createTimeEntry({ memberId, phaseId, hours, date, note }) {
  const fields = {
    'Submitter':         [memberId],
    'Phase Airtable ID': [phaseId],
    'Actual Hours':      hours,
    'Entry Date':        date,
    'Billable':          true,
  }
  if (note) fields['Memo Draft'] = note
  return apiPost(TBL_TIME, fields)
}
